import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type FrameworkKey = 'BANT' | 'MEDDICC' | 'MEDDPICC' | 'SPICED';
interface FrameworkItem {
  letter: string;
  title: string;
  desc: string;
}
const frameworksData: Record<FrameworkKey, FrameworkItem[]> = {
  BANT: [{
    letter: 'B',
    title: 'Budget',
    desc: 'Is there allocated funding for this project?'
  }, {
    letter: 'A',
    title: 'Authority',
    desc: 'Who is the ultimate decision maker?'
  }, {
    letter: 'N',
    title: 'Need',
    desc: 'What is the core business pain or need?'
  }, {
    letter: 'T',
    title: 'Timeline',
    desc: 'When do they need a solution in place?'
  }],
  MEDDICC: [{
    letter: 'M',
    title: 'Metrics',
    desc: 'Quantifiable measures of value.'
  }, {
    letter: 'E',
    title: 'Economic Buyer',
    desc: 'Person with overall budget control.'
  }, {
    letter: 'D',
    title: 'Decision Criteria',
    desc: 'Technical and business requirements.'
  }, {
    letter: 'D',
    title: 'Decision Process',
    desc: 'Steps to reach a final decision.'
  }, {
    letter: 'I',
    title: 'Identify Pain',
    desc: 'Primary business challenges.'
  }, {
    letter: 'C',
    title: 'Champion',
    desc: 'Internal advocate selling on your behalf.'
  }, {
    letter: 'C',
    title: 'Competition',
    desc: 'Alternative solutions being considered.'
  }],
  MEDDPICC: [{
    letter: 'M',
    title: 'Metrics',
    desc: 'Quantifiable measures of value.'
  }, {
    letter: 'E',
    title: 'Economic Buyer',
    desc: 'Person with overall budget control.'
  }, {
    letter: 'D',
    title: 'Decision Criteria',
    desc: 'Technical and business requirements.'
  }, {
    letter: 'D',
    title: 'Decision Process',
    desc: 'Steps to reach a final decision.'
  }, {
    letter: 'P',
    title: 'Paper Process',
    desc: 'Legal, security, and procurement steps.'
  }, {
    letter: 'I',
    title: 'Identify Pain',
    desc: 'Primary business challenges.'
  }, {
    letter: 'C',
    title: 'Champion',
    desc: 'Internal advocate selling on your behalf.'
  }, {
    letter: 'C',
    title: 'Competition',
    desc: 'Alternative solutions being considered.'
  }],
  SPICED: [{
    letter: 'S',
    title: 'Situation',
    desc: 'Current state of the business.'
  }, {
    letter: 'P',
    title: 'Pain',
    desc: 'Problems they are trying to solve.'
  }, {
    letter: 'I',
    title: 'Impact',
    desc: 'Consequences of not solving the pain.'
  }, {
    letter: 'C',
    title: 'Critical Event',
    desc: 'Deadline driving the decision.'
  }, {
    letter: 'D',
    title: 'Decision',
    desc: 'Process and criteria for choosing.'
  }]
};
export function Frameworks() {
  const [activeTab, setActiveTab] = useState<FrameworkKey>('BANT');
  const tabs: FrameworkKey[] = ['BANT', 'MEDDICC', 'MEDDPICC', 'SPICED'];
  return <section id="frameworks" className="py-28 md:py-36 bg-slate-50" data-id="element-120">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12" data-id="element-121">
        <div className="text-center mb-16" data-id="element-122">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-teal-600 mb-4" data-id="element-123">
            Frameworks
          </div>
          <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-6" data-id="element-124">
            Support for every methodology
          </h2>
          <p className="text-base text-slate-600 font-normal max-w-2xl mx-auto" data-id="element-125">
            Map extracted insights directly to your team's qualification
            framework.
          </p>
        </div>

        <div className="max-w-4xl mx-auto" data-id="element-126">
          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 p-1 bg-white border border-slate-200 rounded-xl w-fit mx-auto" data-id="element-127">
            {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-6 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${activeTab === tab ? 'text-teal-600' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-128">
                {activeTab === tab && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal-50 border border-teal-200 rounded-lg" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-129" />}
                <span className="relative z-10" data-id="element-130">{tab}</span>
              </button>)}
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl p-8 md:p-12 border border-slate-200 min-h-[400px]" data-id="element-131">
            <AnimatePresence mode="wait" data-id="element-132">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-133">
                {frameworksData[activeTab].map((item, index) => <div key={index} className="flex items-start space-x-4 bg-slate-50 p-5 rounded-lg border border-slate-200" data-id="element-134">
                    <div className="w-8 h-8 rounded-md bg-teal-50 text-teal-600 font-medium flex items-center justify-center flex-shrink-0 text-sm border border-teal-200" data-id="element-135">
                      {item.letter}
                    </div>
                    <div data-id="element-136">
                      <h4 className="font-medium text-slate-900 mb-1" data-id="element-137">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600 font-normal leading-relaxed" data-id="element-138">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}